import { CartAddEvent, CartErrorEvent } from '@theme/events';

const STORAGE_KEY_LEGACY = 'wishlist:items';
const STORAGE_KEY_GUEST = 'wishlist:items:guest';
const STORAGE_KEY_PENDING_REMOVALS = 'wishlist:pending-removals';
const DEFAULT_WISHLIST_PROXY_PATH = '/apps/wishlist';
const GUEST_PERSISTENCE_MESSAGE = 'Sign in to save your wishlist across devices.';
const TOGGLE_SELECTOR = '[data-wishlist-toggle]';
const ADD_TO_CART_SELECTOR = '[data-wishlist-add-to-cart]';
const LIST_SELECTOR = '[data-wishlist-list]';
const EMPTY_SELECTOR = '[data-wishlist-empty]';
const COUNT_SELECTOR = '[data-wishlist-count]';
const RESULTS_COUNT_SELECTOR = '[data-wishlist-results-count]';
const RESULTS_LABEL_SELECTOR = '[data-wishlist-results-label]';
const SHELL_SELECTOR = '[data-wishlist-shell]';
const STATUS_SELECTOR = '[data-wishlist-status]';
const STORAGE_SCOPE_GUEST = 'guest';
const PROXY_AUTH = {
  UNKNOWN: 'unknown',
  AUTHENTICATED: 'authenticated',
  GUEST: 'guest',
};
/**
 * @typedef {object} WishlistItem
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} vendor
 * @property {string} url
 * @property {string} image
 * @property {string} price
 * @property {string} compareAt
 * @property {string} priceRaw
 * @property {string} compareAtRaw
 * @property {string} collectionHandle
 * @property {string} collectionTitle
 * @property {string} badge
 * @property {string} variantId
 * @property {string} variantsCount
 * @property {string} optionsCount
 * @property {string} available
 */

let proxyAuthState = PROXY_AUTH.UNKNOWN;
let activeStorageScope = STORAGE_SCOPE_GUEST;
let activeStorageKey = STORAGE_KEY_GUEST;
let hasHydratedWishlist = false;
let pendingQuickAddWishlistItemId = '';
let wishlistEnrichmentToken = 0;

const requestIdle = (/** @type {IdleRequestCallback} */ callback) => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback);
    return;
  }

  setTimeout(() => callback(/** @type {IdleDeadline} */ ({ didTimeout: false, timeRemaining: () => 0 })), 1);
};

const getWishlistConfig = () => {
  if (typeof window === 'undefined') return {};
  const config = /** @type {Record<string, unknown> | undefined} */ (window.__wishlistConfig);
  return config && typeof config === 'object' ? config : {};
};

const getWishlistProxyPath = () => {
  const proxyPath = getWishlistConfig().proxyPath;
  return typeof proxyPath === 'string' && proxyPath.trim() ? proxyPath.trim() : DEFAULT_WISHLIST_PROXY_PATH;
};

const normalizeWishlistItem = (/** @type {Partial<WishlistItem> | null | undefined} */ item) => {
  const normalizedId = String(item?.id ?? '').trim();
  if (!normalizedId) return null;

  return {
    id: normalizedId,
    handle: String(item?.handle ?? '').trim(),
    title: String(item?.title ?? '').trim(),
    vendor: String(item?.vendor ?? '').trim(),
    url: String(item?.url ?? '').trim(),
    image: String(item?.image ?? '').trim(),
    price: String(item?.price ?? '').trim(),
    compareAt: String(item?.compareAt ?? '').trim(),
    priceRaw: String(item?.priceRaw ?? '').trim(),
    compareAtRaw: String(item?.compareAtRaw ?? '').trim(),
    collectionHandle: String(item?.collectionHandle ?? '').trim(),
    collectionTitle: String(item?.collectionTitle ?? '').trim(),
    badge: String(item?.badge ?? '').trim(),
    variantId: String(item?.variantId ?? '').trim(),
    variantsCount: String(item?.variantsCount ?? '').trim(),
    optionsCount: String(item?.optionsCount ?? '').trim(),
    available: String(item?.available ?? '').trim(),
  };
};

const normalizeWishlistItems = (/** @type {unknown} */ value) => {
  if (!Array.isArray(value)) return [];

  const seenIds = new Set();
  const normalized = [];

  for (const entry of value) {
    const item = normalizeWishlistItem(/** @type {Partial<WishlistItem>} */ (entry));
    if (!item || seenIds.has(item.id)) continue;
    seenIds.add(item.id);
    normalized.push(item);
  }

  return normalized;
};

const normalizeCustomerId = (/** @type {unknown} */ value) => {
  const normalized = String(value ?? '').trim();
  return /^\d+$/.test(normalized) ? normalized : '';
};

const getStorageKeyForScope = (/** @type {string} */ scope) => {
  if (!scope || scope === STORAGE_SCOPE_GUEST) return STORAGE_KEY_GUEST;
  return `wishlist:items:customer:${scope}`;
};

const getPendingRemovalStorageKeyForScope = (/** @type {string} */ scope = activeStorageScope) => {
  if (!scope || scope === STORAGE_SCOPE_GUEST) return `${STORAGE_KEY_PENDING_REMOVALS}:guest`;
  return `${STORAGE_KEY_PENDING_REMOVALS}:customer:${scope}`;
};

const normalizeProductIds = (/** @type {unknown} */ value) => {
  if (!Array.isArray(value)) return [];
  const ids = value.map((id) => String(id ?? '').trim()).filter(Boolean);
  return [...new Set(ids)];
};

const migrateLegacyGuestWishlist = () => {
  try {
    const legacy = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (!legacy) return;
    if (!localStorage.getItem(STORAGE_KEY_GUEST)) {
      localStorage.setItem(STORAGE_KEY_GUEST, legacy);
    }
    localStorage.removeItem(STORAGE_KEY_LEGACY);
  } catch (error) {
    // Ignore migration errors and continue with scoped keys.
  }
};

const setStorageScope = (/** @type {unknown} */ customerId) => {
  const normalized = normalizeCustomerId(customerId);
  activeStorageScope = normalized || STORAGE_SCOPE_GUEST;
  activeStorageKey = getStorageKeyForScope(activeStorageScope);
  if (activeStorageScope === STORAGE_SCOPE_GUEST) {
    migrateLegacyGuestWishlist();
  }
};

const getPageCustomerId = () => {
  if (typeof window === 'undefined') return '';
  const customerId = /** @type {{ __wishlistCustomerId?: unknown }} */ (window).__wishlistCustomerId;
  return normalizeCustomerId(customerId);
};

const pageHasLoggedInCustomer = () => Boolean(getPageCustomerId());

const syncStorageScopeWithPageContext = () => {
  setStorageScope(getPageCustomerId());
};

syncStorageScopeWithPageContext();

/** @returns {WishlistItem[]} */
const readWishlist = () => {
  try {
    return readWishlistFromKey(activeStorageKey);
  } catch (error) {
    console.warn('Wishlist read failed', error);
    return [];
  }
};

/** @returns {WishlistItem[]} */
const readWishlistFromKey = (/** @type {string} */ key) => {
  const value = localStorage.getItem(key);
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return normalizeWishlistItems(parsed);
  } catch (error) {
    return [];
  }
};

const writeWishlist = (/** @type {WishlistItem[]} */ items) => {
  localStorage.setItem(activeStorageKey, JSON.stringify(normalizeWishlistItems(items)));
};

const readPendingRemovalIds = () => {
  try {
    const value = localStorage.getItem(getPendingRemovalStorageKeyForScope());
    if (!value) return [];
    return normalizeProductIds(JSON.parse(value));
  } catch (error) {
    return [];
  }
};

const writePendingRemovalIds = (/** @type {Array<string>} */ productIds) => {
  const ids = normalizeProductIds(productIds);
  const key = getPendingRemovalStorageKeyForScope();
  if (!ids.length) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(ids));
};

const addPendingRemovalId = (/** @type {string} */ productId) => {
  const id = String(productId || '').trim();
  if (!id) return;
  writePendingRemovalIds([...readPendingRemovalIds(), id]);
};

const removePendingRemovalIds = (/** @type {Array<string>} */ productIds) => {
  const idsToRemove = new Set(normalizeProductIds(productIds));
  if (!idsToRemove.size) return;
  writePendingRemovalIds(readPendingRemovalIds().filter((id) => !idsToRemove.has(id)));
};

const toProductGid = (/** @type {string | number} */ id) => `gid://shopify/Product/${String(id).trim()}`;

const fromProductGid = (/** @type {string} */ gid) => {
  const match = String(gid || '').match(/^gid:\/\/shopify\/Product\/(\d+)$/);
  return match ? match[1] : null;
};

const requestProxy = async (
  /** @type {'GET' | 'POST' | 'DELETE'} */ method,
  /** @type {Record<string, unknown> | null} */ payload = null,
) => {
  try {
    const body = payload ? { ...payload } : null;
    const res = await fetch(getWishlistProxyPath(), {
      method,
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data?.ok === true, status: res.status, data };
  } catch (error) {
    return { ok: false, status: 0, data: {}, error };
  }
};

const isProductGid = (/** @type {string} */ gid) => /^gid:\/\/shopify\/Product\/\d+$/.test(String(gid || '').trim());

const dedupeProductGids = (/** @type {Array<string>} */ gids) => {
  const normalized = gids.map((gid) => String(gid || '').trim()).filter((gid) => isProductGid(gid));
  return [...new Set(normalized)];
};

const normalizeMetafieldProductGids = (/** @type {unknown} */ value) => {
  if (typeof value === 'string') {
    try {
      return normalizeMetafieldProductGids(JSON.parse(value));
    } catch (error) {
      return dedupeProductGids([value]);
    }
  }

  if (Array.isArray(value)) {
    return dedupeProductGids(value.map((entry) => String(entry || '').trim()));
  }

  if (value && typeof value === 'object') {
    const objectValue = /** @type {{ items?: unknown }} */ (value);
    if (Array.isArray(objectValue.items)) {
      return normalizeMetafieldProductGids(objectValue.items);
    }
  }

  return [];
};

const getInitialMetafieldProductGids = () => normalizeMetafieldProductGids(getWishlistConfig().initialItems);

const hydrateFromInitialMetafield = () => {
  if (!pageHasLoggedInCustomer()) return false;
  const initialProductGids = getInitialMetafieldProductGids();
  if (!initialProductGids.length) return false;

  const pendingRemovalIds = new Set(readPendingRemovalIds());
  const nextProductGids = dedupeProductGids([...getLocalProductGids(), ...initialProductGids]).filter((gid) => {
    const id = fromProductGid(gid);
    return !id || !pendingRemovalIds.has(id);
  });

  writeWishlist(mergeItemsFromProductGids(nextProductGids));
  return true;
};

const getLocalProductGids = (/** @type {WishlistItem[]} */ items = readWishlist()) => {
  const gids = items.map((item) => toProductGid(item?.id || ''));
  return dedupeProductGids(gids);
};

const areSameStringLists = (/** @type {Array<string>} */ first, /** @type {Array<string>} */ second) => {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
};

const normalizeProxyProducts = (/** @type {unknown} */ value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return /** @type {Record<string, Record<string, string>>} */ (value);
};

const mergeItemsFromProductGids = (
  /** @type {Array<string>} */ gids,
  /** @type {Record<string, Record<string, string>> | undefined} */ proxyProducts = undefined,
) => {
  const productMap = normalizeProxyProducts(proxyProducts);
  const current = readWishlist();
  const currentById = new Map(current.map((item) => [String(item.id), item]));
  const merged = [];

  for (const gid of gids) {
    const id = fromProductGid(gid);
    if (!id) continue;

    const existing = currentById.get(String(id));
    const fromProxy = productMap[gid];

    const item = {
      handle: '',
      title: '',
      vendor: '',
      url: '',
      image: '',
      price: '',
      compareAt: '',
      priceRaw: '',
      compareAtRaw: '',
      collectionHandle: '',
      collectionTitle: '',
      badge: '',
      variantId: '',
      variantsCount: '',
      optionsCount: '',
      available: '',
      ...(existing || {}),
      ...(fromProxy || {}),
      id: String(id),
    };

    const normalizedItem = normalizeWishlistItem(item);
    if (normalizedItem) {
      merged.push(normalizedItem);
    }
  }

  return merged;
};

const findItemIndex = (/** @type {WishlistItem[]} */ items, /** @type {string} */ id) =>
  items.findIndex((item) => String(item.id) === String(id));

const updateButtonState = (/** @type {Element} */ button, /** @type {boolean} */ isActive) => {
  button.classList.toggle('is-active', isActive);
  button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  button.setAttribute('aria-label', isActive ? 'Remove from wishlist' : 'Add to wishlist');
};

const updateAllButtonsForId = (/** @type {string} */ productId) => {
  const items = readWishlist();
  const isActive = findItemIndex(items, productId) !== -1;
  document.querySelectorAll(`${TOGGLE_SELECTOR}[data-product-id="${productId}"]`).forEach((button) => {
    updateButtonState(button, isActive);
  });
};

/** @returns {WishlistItem | null} */
const getButtonData = (/** @type {HTMLElement} */ button) => {
  const id = button.dataset.productId;
  if (!id) return null;

  return normalizeWishlistItem({
    id,
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    vendor: button.dataset.productVendor || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    compareAt: button.dataset.productCompare || '',
    priceRaw: button.dataset.productPriceRaw || '',
    compareAtRaw: button.dataset.productCompareRaw || '',
    collectionHandle: button.dataset.collectionHandle || '',
    collectionTitle: button.dataset.collectionTitle || '',
    badge: button.dataset.productBadge || '',
    variantId: button.dataset.productVariantId || '',
    variantsCount: button.dataset.productVariantsCount || '',
    optionsCount: button.dataset.productOptionsCount || '',
    available: button.dataset.productAvailable || '',
  });
};

const syncWishlistAfterWrite = (/** @type {string} */ productId, /** @type {WishlistItem[]} */ items) => {
  writeWishlist(items);
  updateAllButtonsForId(productId);
  renderWishlist(items);
};

const removeWishlistItemById = async (
  /** @type {string} */ productId,
  /** @type {{ statusMessage?: string, errorMessage?: string, keepLocalOnProxyFailure?: boolean }} */ options = {},
) => {
  const previousItems = readWishlist();
  const index = findItemIndex(previousItems, productId);
  if (index === -1) return true;

  const items = [...previousItems];
  items.splice(index, 1);

  syncWishlistAfterWrite(productId, items);

  if (options.statusMessage) {
    setWishlistStatus(options.statusMessage);
  }

  if (!pageHasLoggedInCustomer()) {
    proxyAuthState = PROXY_AUTH.GUEST;
    setStorageScope('');
    if (options.statusMessage) {
      setWishlistStatus(`${options.statusMessage} ${GUEST_PERSISTENCE_MESSAGE}`);
    }
    return true;
  }

  if (proxyAuthState === PROXY_AUTH.GUEST) {
    return true;
  }

  const proxyRes = await requestProxy('POST', {
    intent: 'remove',
    productGid: toProductGid(productId),
  });

  if (proxyRes.status === 401) {
    if (!options.keepLocalOnProxyFailure) {
      syncWishlistAfterWrite(productId, previousItems);
    } else {
      addPendingRemovalId(productId);
      setWishlistStatus(options.statusMessage || '');
    }
    if (!options.keepLocalOnProxyFailure && options.errorMessage) {
      setWishlistStatus(options.errorMessage);
    }

    if (pageHasLoggedInCustomer()) {
      proxyAuthState = PROXY_AUTH.UNKNOWN;
    } else {
      proxyAuthState = PROXY_AUTH.GUEST;
      setStorageScope('');
    }
    return Boolean(options.keepLocalOnProxyFailure);
  }

  if (!proxyRes.ok || !Array.isArray(proxyRes.data?.items)) {
    if (!options.keepLocalOnProxyFailure) {
      syncWishlistAfterWrite(productId, previousItems);
    } else {
      addPendingRemovalId(productId);
      setWishlistStatus(options.statusMessage || '');
    }
    if (!options.keepLocalOnProxyFailure && options.errorMessage) {
      setWishlistStatus(options.errorMessage);
    }
    return Boolean(options.keepLocalOnProxyFailure);
  }

  proxyAuthState = PROXY_AUTH.AUTHENTICATED;
  setStorageScope(proxyRes.data?.customerId);
  removePendingRemovalIds([productId]);
  const mergedItems = mergeItemsFromProductGids(proxyRes.data.items, proxyRes.data.products);
  syncWishlistAfterWrite(productId, mergedItems);
  return true;
};

const addWishlistItem = async (
  /** @type {WishlistItem} */ data,
  /** @type {{ keepLocalOnProxyFailure?: boolean }} */ options = {},
) => {
  removePendingRemovalIds([data.id]);
  const previousItems = readWishlist();
  const items = [...previousItems];
  const existingIndex = findItemIndex(items, data.id);

  if (existingIndex === -1) {
    items.unshift(data);
  }

  syncWishlistAfterWrite(data.id, items);

  if (!pageHasLoggedInCustomer()) {
    proxyAuthState = PROXY_AUTH.GUEST;
    setStorageScope('');
    return true;
  }

  if (proxyAuthState === PROXY_AUTH.GUEST) {
    return true;
  }

  const proxyRes = await requestProxy('POST', {
    intent: 'add',
    productGid: toProductGid(data.id),
  });

  if ((proxyRes.status === 401 || proxyRes.status === 0) && !pageHasLoggedInCustomer()) {
    proxyAuthState = PROXY_AUTH.GUEST;
    setStorageScope('');
    return true;
  }

  if (proxyRes.status === 401) {
    if (!options.keepLocalOnProxyFailure) {
      syncWishlistAfterWrite(data.id, previousItems);
    }
    if (pageHasLoggedInCustomer()) {
      proxyAuthState = PROXY_AUTH.UNKNOWN;
    } else {
      proxyAuthState = PROXY_AUTH.GUEST;
      setStorageScope('');
    }
    return Boolean(options.keepLocalOnProxyFailure);
  }

  if (!proxyRes.ok || !Array.isArray(proxyRes.data?.items)) {
    if (!options.keepLocalOnProxyFailure) {
      syncWishlistAfterWrite(data.id, previousItems);
    }
    return Boolean(options.keepLocalOnProxyFailure);
  }

  proxyAuthState = PROXY_AUTH.AUTHENTICATED;
  setStorageScope(proxyRes.data?.customerId);
  const mergedItems = mergeItemsFromProductGids(proxyRes.data.items, proxyRes.data.products);
  syncWishlistAfterWrite(data.id, mergedItems);
  return true;
};

const toggleWishlist = async (/** @type {HTMLElement} */ button) => {
  const data = getButtonData(button);
  if (!data) return;

  const previousItems = readWishlist();
  const items = [...previousItems];
  const index = findItemIndex(items, data.id);
  const isAdd = index === -1;

  if (index === -1) {
    items.unshift(data);
  } else {
    items.splice(index, 1);
  }

  writeWishlist(items);
  updateAllButtonsForId(data.id);
  renderWishlist(items);
  setWishlistStatus(
    isAdd
      ? `${data.title || 'Item'} was added to your wishlist.`
      : `${data.title || 'Item'} was removed from your wishlist.`
  );

  if (!pageHasLoggedInCustomer()) {
    proxyAuthState = PROXY_AUTH.GUEST;
    setStorageScope('');
    setWishlistStatus(
      isAdd
        ? `${data.title || 'Item'} was added to your wishlist. ${GUEST_PERSISTENCE_MESSAGE}`
        : `${data.title || 'Item'} was removed from your wishlist. ${GUEST_PERSISTENCE_MESSAGE}`
    );
    return;
  }

  if (proxyAuthState === PROXY_AUTH.GUEST) return;

  const proxyRes = await requestProxy('POST', {
    intent: isAdd ? 'add' : 'remove',
    productGid: toProductGid(data.id),
  });

  if (proxyRes.status === 401) {
    if (!isAdd) {
      addPendingRemovalId(data.id);
    }
    setWishlistStatus(
      isAdd
        ? `${data.title || 'Item'} was added on this device, but account sync is not available.`
        : `${data.title || 'Item'} was removed on this device, but account sync is not available.`
    );

    if (pageHasLoggedInCustomer()) {
      proxyAuthState = PROXY_AUTH.UNKNOWN;
    } else {
      proxyAuthState = PROXY_AUTH.GUEST;
      setStorageScope('');
    }
    return;
  }

  if (!proxyRes.ok || !Array.isArray(proxyRes.data?.items)) {
    if (!isAdd) {
      addPendingRemovalId(data.id);
    }
    setWishlistStatus(
      isAdd
        ? `${data.title || 'Item'} was added on this device, but account sync failed.`
        : `${data.title || 'Item'} was removed on this device, but account sync failed.`
    );
    return;
  }

  proxyAuthState = PROXY_AUTH.AUTHENTICATED;
  setStorageScope(proxyRes.data?.customerId);
  const mergedItems = mergeItemsFromProductGids(proxyRes.data.items, proxyRes.data.products);
  writeWishlist(mergedItems);
  updateAllButtonsForId(data.id);
  renderWishlist(mergedItems);
};

const getDiscountPercent = (/** @type {WishlistItem} */ item) => {
  const rawPrice = parseNumber(item.priceRaw);
  const rawCompare = parseNumber(item.compareAtRaw);
  if (!rawPrice || !rawCompare) return null;
  if (rawCompare <= rawPrice) return null;
  return Math.round((1 - rawPrice / rawCompare) * 100);
};

const parseNumber = (/** @type {string | number | undefined} */ value) => {
  if (value === null || value === undefined || value === '') return null;
  const raw = typeof value === 'number' ? String(value) : value;
  if (typeof raw !== 'string') return null;
  const cleaned = raw.replace(/[^0-9,.-]/g, '');
  if (!cleaned) return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized = cleaned;

  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  } else if (lastComma > -1) {
    const digitsAfter = cleaned.length - lastComma - 1;
    if (digitsAfter > 0 && digitsAfter <= 2) {
      normalized = cleaned.replace(',', '.');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  }

  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) return null;
  return parsed;
};

const getWishlistFormatter = () => {
  const list = document.querySelector(LIST_SELECTOR);
  if (!(list instanceof HTMLElement)) return null;
  const currency = list.dataset.wishlistCurrency || '';
  const locale = list.dataset.wishlistLocale || document.documentElement.lang || 'en';
  if (!currency) return null;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    });
  } catch (error) {
    return null;
  }
};

const getWishlistMoneyDivisor = () => {
  const formatter = getWishlistFormatter();
  if (!formatter) return 100;

  const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return 10 ** fractionDigits;
};

const normalizeWishlistMoneyAmount = (/** @type {unknown} */ amount) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return null;
  return numericAmount / getWishlistMoneyDivisor();
};

const formatWishlistCurrencyAmount = (
  /** @type {number} */ amount,
  /** @type {Intl.NumberFormat} */ formatter,
) => {
  const parts = formatter.formatToParts(amount);
  let output = '';

  parts.forEach((part, index) => {
    const previousPart = index > 0 ? parts[index - 1] : null;
    const nextPart = index < parts.length - 1 ? parts[index + 1] : null;
    const touchesNumber =
      (previousPart?.type === 'currency' && (part.type === 'integer' || part.type === 'fraction')) ||
      (part.type === 'currency' && (nextPart?.type === 'integer' || nextPart?.type === 'fraction'));

    if (touchesNumber && output && !output.endsWith(' ')) {
      output += ' ';
    }

    output += part.value;
  });

  return output;
};

const formatWishlistPrice = (
  /** @type {WishlistItem} */ item,
  /** @type {Intl.NumberFormat | null} */ formatter,
) => {
  if (!formatter) return item.price || '';
  const rawNumber = parseNumber(item.priceRaw);
  if (rawNumber === null) return item.price || '';
  return formatWishlistCurrencyAmount(rawNumber, formatter);
};

const formatWishlistCompareAt = (
  /** @type {WishlistItem} */ item,
  /** @type {Intl.NumberFormat | null} */ formatter,
) => {
  if (!formatter) return item.compareAt || '';
  const rawNumber = parseNumber(item.compareAtRaw);
  if (rawNumber === null) return item.compareAt || '';
  return formatWishlistCurrencyAmount(rawNumber, formatter);
};

const getWishlistShell = () => document.querySelector(SHELL_SELECTOR);

const updateWishlistShellLoading = (/** @type {boolean} */ isLoading) => {
  const shell = getWishlistShell();
  if (!(shell instanceof HTMLElement)) return;
  shell.dataset.loading = isLoading ? 'true' : 'false';
};

const setPendingQuickAddWishlistItem = (/** @type {string} */ itemId) => {
  pendingQuickAddWishlistItemId = String(itemId || '').trim();
};

const clearPendingQuickAddWishlistItem = () => {
  pendingQuickAddWishlistItemId = '';
};

const setWishlistStatus = (/** @type {string} */ message) => {
  const status = document.querySelector(STATUS_SELECTOR);
  if (!(status instanceof HTMLElement)) return;
  status.textContent = '';
  if (!message) return;

  window.requestAnimationFrame(() => {
    status.textContent = message;
  });
};

const updateWishlistResultsCount = (/** @type {number} */ count) => {
  document.querySelectorAll(RESULTS_COUNT_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = String(count);
  });

  document.querySelectorAll(RESULTS_LABEL_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = count === 1 ? 'Result' : 'Results';
  });
};

const createWishlistButton = (
  /** @type {{ text?: string, ariaLabel?: string, disabled?: boolean } | undefined} */ options = {},
) => {
  const { text = '', ariaLabel = '', disabled = false } = options;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'wishlist-card__button';
  button.textContent = text;
  button.disabled = Boolean(disabled);
  if (ariaLabel) {
    button.setAttribute('aria-label', ariaLabel);
  }
  return button;
};

const applyWishlistDataAttributes = (/** @type {HTMLElement} */ element, /** @type {WishlistItem} */ item) => {
  element.dataset.productId = item.id;
  element.dataset.productHandle = item.handle || '';
  element.dataset.productTitle = item.title || '';
  element.dataset.productVendor = item.vendor || '';
  element.dataset.productUrl = item.url || '';
  element.dataset.productImage = item.image || '';
  element.dataset.productBadge = item.badge || '';
  element.dataset.productPrice = item.price || '';
  element.dataset.productPriceRaw = item.priceRaw || '';
  element.dataset.productVariantId = item.variantId || '';
  element.dataset.productVariantsCount = item.variantsCount || '';
  element.dataset.productOptionsCount = item.optionsCount || '';
  element.dataset.productAvailable = item.available || '';

  if (item.compareAt) {
    element.dataset.productCompare = item.compareAt;
  }

  if (item.compareAtRaw) {
    element.dataset.productCompareRaw = item.compareAtRaw;
  }

  if (item.collectionHandle) {
    element.dataset.collectionHandle = item.collectionHandle;
  }

  if (item.collectionTitle) {
    element.dataset.collectionTitle = item.collectionTitle;
  }
};

const resolveWishlistHandle = (/** @type {WishlistItem} */ item) => {
  if (item.handle) return item.handle;
  if (!item.url) return '';

  try {
    const resolvedUrl = new URL(item.url, window.location.origin);
    const match = resolvedUrl.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1] || '') : '';
  } catch (error) {
    return '';
  }
};

const formatWishlistAmount = (/** @type {number | null} */ amount, /** @type {Intl.NumberFormat | null} */ formatter) => {
  if (amount === null || !formatter) return '';
  return formatter.format(amount);
};

const createWishlistProductUrl = (/** @type {string} */ handle, /** @type {string} */ variantId) => {
  if (!handle) return '';
  const productUrl = `/products/${encodeURIComponent(handle)}`;
  if (!variantId) return productUrl;
  return `${productUrl}?variant=${encodeURIComponent(variantId)}`;
};

const areWishlistItemsEqual = (/** @type {WishlistItem} */ first, /** @type {WishlistItem} */ second) => {
  return (
    first.id === second.id &&
    first.handle === second.handle &&
    first.title === second.title &&
    first.vendor === second.vendor &&
    first.url === second.url &&
    first.image === second.image &&
    first.price === second.price &&
    first.compareAt === second.compareAt &&
    first.priceRaw === second.priceRaw &&
    first.compareAtRaw === second.compareAtRaw &&
    first.collectionHandle === second.collectionHandle &&
    first.collectionTitle === second.collectionTitle &&
    first.badge === second.badge &&
    first.variantId === second.variantId &&
    first.variantsCount === second.variantsCount &&
    first.optionsCount === second.optionsCount &&
    first.available === second.available
  );
};

const isTruthyWishlistValue = (/** @type {string | undefined} */ value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const isAvailableWishlistItem = (/** @type {WishlistItem} */ item) => {
  if (!item.available) return true;
  return isTruthyWishlistValue(item.available);
};

const requiresWishlistQuickAdd = (/** @type {WishlistItem} */ item) => {
  const variantsCount = Number(item.variantsCount || '0');
  const optionsCount = Number(item.optionsCount || '0');
  return variantsCount > 1 || optionsCount > 1;
};

const updateWishlistItem = (
  /** @type {string} */ itemId,
  /** @type {Partial<WishlistItem>} */ patch,
  /** @type {{ render?: boolean }} */ options = {},
) => {
  const items = readWishlist();
  const index = findItemIndex(items, itemId);
  if (index === -1) return null;

  const nextItem = normalizeWishlistItem({
    ...items[index],
    ...patch,
  });

  if (!nextItem) return null;

  items.splice(index, 1, nextItem);
  writeWishlist(items);

  if (options.render) {
    renderWishlist(items);
  }

  return nextItem;
};

const productRequestCache = new Map();

const fetchWishlistProductData = async (/** @type {WishlistItem} */ item) => {
  const handle = resolveWishlistHandle(item);
  if (!handle) return null;

  const cacheKey = handle;
  if (productRequestCache.has(cacheKey)) {
    return productRequestCache.get(cacheKey);
  }

  const request = fetch(`/products/${encodeURIComponent(handle)}.js`)
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Product request failed with status ${response.status}`);
      }

      const product = await response.json();
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const requestedVariantId = String(item.variantId || '').trim();
      const matchedVariant = requestedVariantId
        ? variants.find(
            (/** @type {{ id?: string | number, available?: boolean, price?: unknown, compare_at_price?: unknown }} */ variant) =>
              String(variant?.id || '') === requestedVariantId,
          )
        : null;
      const defaultVariant =
        matchedVariant ||
        variants.find(
          (/** @type {{ id?: string | number, available?: boolean, price?: unknown, compare_at_price?: unknown }} */ variant) =>
            Boolean(variant?.available),
        ) ||
        variants[0] ||
        null;
      const variantId = defaultVariant?.id ? String(defaultVariant.id) : requestedVariantId;
      const priceAmount = normalizeWishlistMoneyAmount(defaultVariant?.price ?? product?.price ?? 0);
      const compareAmount = normalizeWishlistMoneyAmount(defaultVariant?.compare_at_price ?? product?.compare_at_price ?? 0);
      const resolvedPriceAmount = Number.isFinite(priceAmount) ? priceAmount : null;
      const resolvedCompareAmount =
        compareAmount !== null && resolvedPriceAmount !== null && compareAmount > resolvedPriceAmount
          ? compareAmount
          : null;

      return {
        id: String(product?.id ?? item.id),
        handle: String(product?.handle ?? handle),
        title: String(product?.title ?? item.title ?? ''),
        vendor: String(product?.vendor ?? item.vendor ?? ''),
        url: createWishlistProductUrl(String(product?.handle ?? handle), variantId) || String(product?.url ?? item.url ?? ''),
        image: String(product?.featured_image ?? item.image ?? ''),
        priceAmount: resolvedPriceAmount,
        compareAmount: resolvedCompareAmount,
        variantId,
        variantsCount: String(variants.length || item.variantsCount || ''),
        optionsCount: String(Array.isArray(product?.options) ? product.options.length : item.optionsCount || ''),
        available: String(Boolean(product?.available ?? defaultVariant?.available ?? true)),
      };
    })
    .catch((error) => {
      productRequestCache.delete(cacheKey);
      throw error;
    });

  productRequestCache.set(cacheKey, request);
  return request;
};

const enrichWishlistItemsForGrid = async (/** @type {WishlistItem[]} */ items) => {
  const normalizedItems = normalizeWishlistItems(items);
  if (!normalizedItems.length) return;

  const token = ++wishlistEnrichmentToken;
  const formatter = getWishlistFormatter();

  const enrichedItems = await Promise.all(
    normalizedItems.map(async (item) => {
      try {
        const enriched = await fetchWishlistProductData(item);
        if (!enriched) return item;

        const nextItem = normalizeWishlistItem({
          ...item,
          id: enriched.id || item.id,
          handle: enriched.handle || item.handle,
          title: enriched.title || item.title,
          vendor: enriched.vendor || item.vendor,
          url: enriched.url || item.url,
          image: enriched.image || item.image,
          price: formatWishlistAmount(enriched.priceAmount, formatter) || item.price,
          compareAt: formatWishlistAmount(enriched.compareAmount, formatter),
          priceRaw: enriched.priceAmount !== null ? String(enriched.priceAmount) : item.priceRaw,
          compareAtRaw: enriched.compareAmount !== null ? String(enriched.compareAmount) : '',
          variantId: enriched.variantId || item.variantId,
          variantsCount: enriched.variantsCount || item.variantsCount,
          optionsCount: enriched.optionsCount || item.optionsCount,
          available: enriched.available || item.available,
        });

        return nextItem || item;
      } catch (error) {
        console.error('Failed to enrich wishlist grid item', error);
        return item;
      }
    })
  );

  if (token !== wishlistEnrichmentToken) return;

  const hasChanges = enrichedItems.some((item, index) => {
    const normalizedItem = normalizedItems[index];
    if (!normalizedItem) return true;
    return !areWishlistItemsEqual(item, normalizedItem);
  });
  if (!hasChanges) return;

  writeWishlist(enrichedItems);
  renderWishlist(enrichedItems);
};

const getWishlistItemById = (/** @type {string} */ itemId) => {
  const items = readWishlist();
  const index = findItemIndex(items, itemId);
  return index === -1 ? null : items[index] || null;
};

const ensureWishlistCartData = async (/** @type {WishlistItem} */ item) => {
  if (item.variantId && item.variantsCount && item.optionsCount && item.available) {
    return item;
  }

  try {
    const enriched = await fetchWishlistProductData(item);
    if (!enriched) return item;

    return (
      updateWishlistItem(item.id, enriched, {
        render: true,
      }) || item
    );
  } catch (error) {
    console.error('Failed to enrich wishlist item', error);
    return item;
  }
};

const openWishlistQuickAdd = (/** @type {string} */ itemId) => {
  window.requestAnimationFrame(() => {
    const button = document.querySelector(
      `${LIST_SELECTOR} [data-wishlist-item-id="${itemId}"] quick-add-component button[on\\:click]`
    );

    if (button instanceof HTMLButtonElement) {
      button.click();
    }
  });
};

const createWishlistRemoveButton = (/** @type {WishlistItem} */ item) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'wishlist-card__remove';
  button.dataset.wishlistToggle = '';
  applyWishlistDataAttributes(button, item);
  button.setAttribute('aria-label', `Remove ${item.title || 'item'} from wishlist`);
  button.innerHTML =
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 3.5 12.5 12.5M12.5 3.5 3.5 12.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg><span class="visually-hidden">Remove</span>';
  return button;
};

const createWishlistAction = (/** @type {WishlistItem} */ item, /** @type {boolean} */ useQuickAdd) => {
  if (useQuickAdd) {
    const quickAdd = document.createElement('quick-add-component');
    quickAdd.className = 'wishlist-card__actions';
    quickAdd.dataset.productBadge = item.badge || '';
    quickAdd.dataset.productTitle = item.title || '';
    quickAdd.dataset.productVendor = item.vendor || '';
    quickAdd.dataset.quickAddButton = 'choose';
    quickAdd.dataset.productOptionsCount = item.optionsCount || '';

    const button = createWishlistButton({
      text: 'Add To Cart',
      ariaLabel: `Choose options for ${item.title || 'product'}`,
    });
    button.setAttribute('on:click', 'quick-add-component/handleClick');
    button.dataset.wishlistQuickAdd = '';
    applyWishlistDataAttributes(button, item);

    quickAdd.appendChild(button);
    return quickAdd;
  }

  const actions = document.createElement('div');
  actions.className = 'wishlist-card__actions';

  const button = createWishlistButton({
    text: isAvailableWishlistItem(item) ? 'Add To Cart' : 'Sold Out',
    ariaLabel: isAvailableWishlistItem(item)
      ? `Add ${item.title || 'product'} to cart`
      : `${item.title || 'Product'} is sold out`,
    disabled: !isAvailableWishlistItem(item),
  });
  button.dataset.wishlistAddToCart = '';
  applyWishlistDataAttributes(button, item);

  actions.appendChild(button);
  return actions;
};

const createWishlistItem = (/** @type {WishlistItem} */ item, /** @type {Intl.NumberFormat | null} */ formatter) => {
  const listItem = document.createElement('li');
  listItem.className = 'wishlist-card';
  listItem.dataset.wishlistItemId = item.id;
  const useQuickAdd = requiresWishlistQuickAdd(item);

  const frame = document.createElement('article');
  frame.className = 'wishlist-card__frame';

  const media = document.createElement('div');
  media.className = 'wishlist-card__media';
  media.appendChild(createWishlistRemoveButton(item));

  const mediaLink = document.createElement('a');
  mediaLink.className = 'wishlist-card__media-link';
  mediaLink.href = item.url || '#';
  mediaLink.setAttribute('aria-label', item.title || 'View product');

  if (item.image) {
    const image = document.createElement('img');
    image.className = 'wishlist-card__image';
    image.src = item.image;
    image.alt = item.title || '';
    image.loading = 'lazy';
    mediaLink.appendChild(image);
  }

  media.appendChild(mediaLink);

  const meta = document.createElement('div');
  meta.className = 'wishlist-card__meta';

  const badge = document.createElement('div');
  badge.className = 'wishlist-card__badge';
  badge.textContent = item.badge || '';

  const brand = document.createElement('p');
  brand.className = 'wishlist-card__brand';
  brand.textContent = item.vendor || '';
  if (!item.vendor) {
    brand.hidden = true;
  }

  const title = document.createElement('p');
  title.className = 'wishlist-card__title';

  const titleLink = document.createElement('a');
  titleLink.href = item.url || '#';
  titleLink.textContent = item.title || 'Untitled product';
  title.appendChild(titleLink);

  const price = document.createElement('div');
  price.className = 'wishlist-card__price';

  const currentPrice = document.createElement('span');
  currentPrice.className = 'wishlist-card__price-current';
  currentPrice.textContent = formatWishlistPrice(item, formatter);
  price.appendChild(currentPrice);

  const compareText = formatWishlistCompareAt(item, formatter);
  if (compareText) {
    const compare = document.createElement('span');
    compare.className = 'wishlist-card__price-compare';
    compare.textContent = compareText;
    price.appendChild(compare);
  }

  const discount = getDiscountPercent(item);
  if (discount) {
    const discountElement = document.createElement('span');
    discountElement.className = 'wishlist-card__price-discount';
    discountElement.textContent = `(-${discount}%)`;
    price.appendChild(discountElement);
  }

  meta.appendChild(badge);
  meta.appendChild(brand);
  meta.appendChild(title);
  meta.appendChild(price);

  frame.appendChild(media);
  frame.appendChild(meta);
  frame.appendChild(createWishlistAction(item, useQuickAdd));

  if (useQuickAdd) {
    const productCard = document.createElement('product-card');
    productCard.className = 'product-card product-card--wishlist';
    productCard.dataset.productId = item.id;

    const productLink = document.createElement('a');
    productLink.href = item.url || '#';
    productLink.className = 'visually-hidden';
    productLink.setAttribute('ref', 'productCardLink');
    productLink.setAttribute('id', `wishlist-product-card-${item.id}`);
    productLink.textContent = item.title || 'Wishlist product';

    productCard.appendChild(productLink);
    productCard.appendChild(frame);
    listItem.appendChild(productCard);
    return listItem;
  }

  listItem.appendChild(frame);
  return listItem;
};

const addWishlistItemToCart = async (/** @type {WishlistItem} */ item) => {
  const variantId = Number(item.variantId || '0');
  if (!variantId) {
    throw new Error('Missing variant id');
  }

  const addResponse = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    }),
  });

  const addPayload = await addResponse.json().catch(() => ({}));

  if (!addResponse.ok || addPayload?.status) {
    const errorMessage = addPayload?.description || addPayload?.message || 'Unable to add this item to your cart.';
    document.dispatchEvent(new CartErrorEvent(String(variantId), errorMessage, addPayload?.description, addPayload?.errors));
    throw new Error(errorMessage);
  }

  let cart = null;

  try {
    const cartResponse = await fetch('/cart.js');
    if (cartResponse.ok) {
      cart = await cartResponse.json();
    }
  } catch (error) {
    console.error('Failed to refresh cart after wishlist add', error);
  }

  const removalPromise = removeWishlistItemById(item.id, {
    statusMessage: `${item.title || 'Item'} was added to your cart and removed from your wishlist.`,
    errorMessage: `We added ${item.title || 'this item'} to your cart, but couldn't remove it from your wishlist.`,
    keepLocalOnProxyFailure: true,
  });

  document.dispatchEvent(
    new CartAddEvent(cart || {}, String(variantId), {
      source: 'wishlist-page',
      itemCount: cart?.item_count ?? 1,
      productId: item.id,
      variantId: String(variantId),
    })
  );

  await removalPromise;
};

const renderWishlist = (/** @type {WishlistItem[]} */ items = readWishlist()) => {
  const list = document.querySelector(LIST_SELECTOR);
  const empty = document.querySelector(EMPTY_SELECTOR);
  const normalizedItems = normalizeWishlistItems(items);
  updateWishlistCount(normalizedItems.length);
  updateWishlistShellLoading(false);
  if (!(list instanceof HTMLElement)) return;

  const formatter = getWishlistFormatter();

  list.innerHTML = '';

  if (!normalizedItems.length) {
    list.hidden = true;
    if (empty instanceof HTMLElement) empty.hidden = false;
    return;
  }

  if (empty instanceof HTMLElement) empty.hidden = true;
  list.hidden = false;

  const fragment = document.createDocumentFragment();
  normalizedItems.forEach((item) => {
    fragment.appendChild(createWishlistItem(item, formatter));
  });
  list.appendChild(fragment);

  if (hasHydratedWishlist) {
    void enrichWishlistItemsForGrid(normalizedItems);
  }
};

const updateWishlistCount = (/** @type {number} */ count) => {
  let resolvedCount = count;
  if (hasHydratedWishlist && resolvedCount <= 0 && pageHasLoggedInCustomer()) {
    const pageCustomerId = getPageCustomerId();
    const pageStorageKey = getStorageKeyForScope(pageCustomerId);
    if (pageStorageKey !== activeStorageKey) {
      try {
        const fallbackItems = readWishlistFromKey(pageStorageKey);
        if (Array.isArray(fallbackItems) && fallbackItems.length > 0) {
          activeStorageScope = pageCustomerId;
          activeStorageKey = pageStorageKey;
          resolvedCount = fallbackItems.length;
        }
      } catch (error) {
        // Ignore fallback read errors and continue with current count.
      }
    }
  }

  document.querySelectorAll(COUNT_SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.textContent = String(resolvedCount);
    el.hidden = resolvedCount <= 0;
  });

  updateWishlistResultsCount(resolvedCount);
};

const syncWishlistCount = () => {
  const items = readWishlist();
  updateWishlistCount(items.length);
};

const runWishlistUiSync = (/** @type {ParentNode} */ root = document) => {
  const syncRoot = root && typeof root.querySelectorAll === 'function' ? root : document;
  initButtons(syncRoot);
  if (syncRoot === document) {
    syncWishlistCount();
  }
};

const initButtons = (/** @type {ParentNode} */ root = document) => {
  const items = readWishlist();
  if (root === document) {
    updateWishlistCount(items.length);
  }

  const buttons = [
    ...(root instanceof Element && root.matches(TOGGLE_SELECTOR) ? [root] : []),
    ...root.querySelectorAll(TOGGLE_SELECTOR),
  ];

  buttons.forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    const data = getButtonData(button);
    if (!data) return;
    const isActive = findItemIndex(items, data.id) !== -1;
    updateButtonState(button, isActive);
  });
};

const handleClick = (/** @type {MouseEvent} */ event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest(TOGGLE_SELECTOR);
  if (button instanceof HTMLElement) {
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist(button);
    return;
  }

  const quickAddTrigger = event.target.closest('[data-wishlist-quick-add]');
  if (quickAddTrigger instanceof HTMLElement) {
    const quickAddItem = getButtonData(quickAddTrigger) || getWishlistItemById(quickAddTrigger.dataset.productId || '');
    if (quickAddItem) {
      setPendingQuickAddWishlistItem(quickAddItem.id);
    }
    return;
  }

  const addToCartButton = event.target.closest(ADD_TO_CART_SELECTOR);
  if (!(addToCartButton instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();

  const currentItem = getButtonData(addToCartButton) || getWishlistItemById(addToCartButton.dataset.productId || '');
  if (!currentItem) return;

  void (async () => {
    addToCartButton.classList.add('is-loading');
    addToCartButton.disabled = true;
    let resolvedItem = currentItem;

    try {
      const enrichedItem = await ensureWishlistCartData(currentItem);
      resolvedItem = enrichedItem;
      if (!isAvailableWishlistItem(enrichedItem)) {
        setWishlistStatus(`${enrichedItem.title || 'This item'} is currently sold out.`);
        return;
      }

      if (requiresWishlistQuickAdd(enrichedItem)) {
        openWishlistQuickAdd(enrichedItem.id);
        setWishlistStatus(`Choose your preferred options for ${enrichedItem.title || 'this product'}.`);
        return;
      }

      await addWishlistItemToCart(enrichedItem);
    } catch (error) {
      console.error('Wishlist add to cart failed', error);
      setWishlistStatus(`We couldn't add ${currentItem.title || 'this item'} to your cart.`);
    } finally {
      addToCartButton.classList.remove('is-loading');
      addToCartButton.disabled = !isAvailableWishlistItem(resolvedItem);
    }
  })();
};

document.addEventListener('click', handleClick);

const handleWishlistCartUpdate = (/** @type {Event & { detail?: { data?: Record<string, unknown> } }} */ event) => {
  if (!pendingQuickAddWishlistItemId) return;
  if (event.detail?.data?.didError) return;

  const source = String(event.detail?.data?.source || '');
  const productId = String(event.detail?.data?.productId || '').trim();

  if (source !== 'product-form-component') return;
  if (!productId || productId !== pendingQuickAddWishlistItemId) return;

  const pendingItem = getWishlistItemById(productId);
  clearPendingQuickAddWishlistItem();

  void removeWishlistItemById(productId, {
    statusMessage: `${pendingItem?.title || 'Item'} was added to your cart and removed from your wishlist.`,
    errorMessage: `We added ${pendingItem?.title || 'this item'} to your cart, but couldn't remove it from your wishlist.`,
    keepLocalOnProxyFailure: true,
  });
};

document.addEventListener(CartAddEvent.eventName, handleWishlistCartUpdate);

const initWishlistQuickAddDialogTracking = () => {
  const quickAddDialog = document.getElementById('quick-add-dialog');
  if (!(quickAddDialog instanceof HTMLElement)) return;
  if (quickAddDialog.dataset.wishlistQuickAddTracking === 'true') return;

  quickAddDialog.dataset.wishlistQuickAddTracking = 'true';
  quickAddDialog.addEventListener('dialog:close', clearPendingQuickAddWishlistItem);
};

const hydrateFromProxy = async () => {
  updateWishlistShellLoading(true);
  const localItems = readWishlist();
  updateWishlistCount(localItems.length);
  const localProductGids = getLocalProductGids(localItems);

  const proxyRes = await requestProxy('GET');
  if (proxyRes.status === 401) {
    if (pageHasLoggedInCustomer()) {
      proxyAuthState = PROXY_AUTH.UNKNOWN;
      hydrateFromInitialMetafield();
    } else {
      proxyAuthState = PROXY_AUTH.GUEST;
      setStorageScope('');
    }
    return;
  }
  if (!proxyRes.ok || !Array.isArray(proxyRes.data?.items)) {
    hydrateFromInitialMetafield();
    return;
  }
  proxyAuthState = PROXY_AUTH.AUTHENTICATED;
  setStorageScope(proxyRes.data?.customerId);

  const pendingRemovalIds = readPendingRemovalIds();
  const pendingRemovalIdSet = new Set(pendingRemovalIds);
  const filterPendingRemovals = (/** @type {Array<string>} */ gids) =>
    gids.filter((gid) => {
      const id = fromProductGid(gid);
      return !id || !pendingRemovalIdSet.has(id);
    });

  const serverProductGids = dedupeProductGids(proxyRes.data.items);
  const serverProductGidsAfterPendingRemovals = filterPendingRemovals(serverProductGids);
  const localProductGidsAfterPendingRemovals = filterPendingRemovals(localProductGids);
  const nextProductGidsAfterPendingRemovals = dedupeProductGids([
    ...localProductGidsAfterPendingRemovals,
    ...serverProductGidsAfterPendingRemovals,
  ]);

  let syncedProductGids = serverProductGidsAfterPendingRemovals;
  let syncedProducts = proxyRes.data.products;
  if (!areSameStringLists(serverProductGids, nextProductGidsAfterPendingRemovals)) {
    const syncRes = await requestProxy('POST', {
      intent: 'replace',
      items: nextProductGidsAfterPendingRemovals,
    });

    if (syncRes.status === 401) {
      if (pageHasLoggedInCustomer()) {
        proxyAuthState = PROXY_AUTH.UNKNOWN;
      } else {
        proxyAuthState = PROXY_AUTH.GUEST;
        setStorageScope('');
      }
      return;
    }

    if (syncRes.ok && Array.isArray(syncRes.data?.items)) {
      const rawSyncedProductGids = dedupeProductGids(syncRes.data.items);
      syncedProductGids = filterPendingRemovals(rawSyncedProductGids);
      syncedProducts = syncRes.data.products;
      const syncedProductIds = new Set(rawSyncedProductGids.map((gid) => fromProductGid(gid)).filter(Boolean));
      removePendingRemovalIds(pendingRemovalIds.filter((id) => !syncedProductIds.has(id)));
    } else {
      syncedProductGids = nextProductGidsAfterPendingRemovals;
    }
  } else if (pendingRemovalIds.length) {
    const syncedProductIds = new Set(syncedProductGids.map((gid) => fromProductGid(gid)).filter(Boolean));
    removePendingRemovalIds(pendingRemovalIds.filter((id) => !syncedProductIds.has(id)));
  }

  const mergedItems = mergeItemsFromProductGids(syncedProductGids, syncedProducts);
  writeWishlist(mergedItems);
};

const bootstrapWishlist = async () => {
  try {
    initWishlistQuickAddDialogTracking();
    await hydrateFromProxy();
  } finally {
    hasHydratedWishlist = true;
    initButtons();
    renderWishlist();

    if (needsPostHydrationSync) {
      needsPostHydrationSync = false;
      scheduleSync();
    }

    [120, 450, 1200].forEach((delay) => {
      window.setTimeout(() => {
        if (!hasHydratedWishlist) return;
        runWishlistUiSync();
      }, delay);
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void bootstrapWishlist();
  });
} else {
  void bootstrapWishlist();
}

window.addEventListener('pageshow', () => runWishlistUiSync());
window.addEventListener('focus', () => runWishlistUiSync());
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) runWishlistUiSync();
});
window.addEventListener('storage', (/** @type {StorageEvent} */ event) => {
  if (event.key === activeStorageKey) {
    runWishlistUiSync();
    return;
  }
  if (activeStorageScope === STORAGE_SCOPE_GUEST && event.key === STORAGE_KEY_LEGACY) {
    migrateLegacyGuestWishlist();
    runWishlistUiSync();
  }
});

let isSyncScheduled = false;
let needsPostHydrationSync = false;
/** @type {Set<ParentNode>} */
const pendingSyncRoots = new Set();
const scheduleSync = (/** @type {ParentNode} */ root = document) => {
  if (!hasHydratedWishlist) {
    needsPostHydrationSync = true;
    return;
  }
  pendingSyncRoots.add(root);
  if (isSyncScheduled) return;
  isSyncScheduled = true;
  requestIdle(() => {
    isSyncScheduled = false;
    const roots = Array.from(pendingSyncRoots);
    pendingSyncRoots.clear();

    if (roots.includes(document)) {
      runWishlistUiSync();
      return;
    }

    syncWishlistCount();
    for (const pendingRoot of roots) {
      runWishlistUiSync(pendingRoot);
    }
  });
};

if (typeof window !== 'undefined') {
  window.__darveysWishlist = {
    addItemFromElement: async (/** @type {HTMLElement} */ element) => {
      const data = getButtonData(element);
      if (!data) return false;
      return addWishlistItem(data, {
        keepLocalOnProxyFailure: true,
      });
    },
    syncUi: () => {
      runWishlistUiSync();
    },
  };
}

const WISHLIST_UI_SELECTOR = `${COUNT_SELECTOR}, ${TOGGLE_SELECTOR}`;

const isWishlistUiElement = (/** @type {Element} */ element) =>
  element.matches(WISHLIST_UI_SELECTOR) || Boolean(element.closest(WISHLIST_UI_SELECTOR));

const hasWishlistUiDescendant = (/** @type {Element} */ element) =>
  Boolean(element.querySelector(WISHLIST_UI_SELECTOR));

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type !== 'childList') continue;

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.closest(LIST_SELECTOR)) continue;
      if (isWishlistUiElement(node) || hasWishlistUiDescendant(node)) {
        scheduleSync(node);
        return;
      }
    }
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});
